import React, { useState, useEffect, useRef } from "react";
import { findCards } from "../../shared";
import { searchOnePiece, printLabel } from "../onepiece/onePieceIndex";
import { loadSets, knownSets } from "../onepiece/onePieceSets";
import { loadDon, knownDon, searchDon } from "../onepiece/onePieceDon";
import { opImageUrl, DON_ART } from "../onepiece/onePieceCards";

/**
 * Finding a real card, for anywhere that needs one.
 *
 * Lifted out of the stack builder rather than written twice. The two screens
 * that want a card want the same thing — search, see the art, press it — and
 * the awkward parts are the same in both: one game answers from a bundled
 * catalogue and the other over the network, and roughly one card in seven has
 * no picture at the source. Solving that once is the point.
 *
 * It only finds. What happens to the card afterwards belongs to whoever
 * rendered this, which is why a stack can count copies and a submission can
 * fill in a row without either knowing about the other.
 */

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;
const MAX_SHOWN = 24;

/** One card in a form both games can be used in. */
export const asItem = (card, isOnePiece) =>
  /* A DON!! card has no number printed on it, so the set is the only thing
     that tells two of them apart on sight, and its art is hosted by the price
     source rather than built from a code like every other card here. */
  card?.don
    ? {
        key: card.id,
        name: card.name,
        number: card.id,           // still the identity, and still what prices
        /* And nothing to write in a "card number" box, because nothing is
           printed on one. The id is ours, invented so the card can be stored
           at all; showing it to somebody logging a submission would be the
           app's bookkeeping presented as a fact about the card. */
        printed: "",
        set: card.set || "DON!!",
        image: card.image || DON_ART,
      }
    : isOnePiece
    ? {
        key: card.id,
        name: card.name,
        number: card.id,           // the code is the identity, and what prices
        set: printLabel(card.id, card.rarity),
        image: opImageUrl(card.id),
      }
    : {
        key: card.id,
        name: card.name,
        number: card.cardNumber,
        set: card.setName,
        image: card.imageUrl,
      };

/**
 * Card art, which is a URL built from the set and the number rather than
 * fetched — that is what makes the search fast, and it is also why about one
 * card in seven has no picture at the source.
 *
 * A missing picture and an unreachable one look identical to an <img> and are
 * not the same thing at all. The source spent part of an afternoon answering
 * 503, and every card asked for during that window went blank and stayed
 * blank: the failure was remembered, nothing ever asked again, and the same
 * file fetched by hand minutes later came back in 50ms. One bad few minutes
 * cost a whole session's art.
 *
 * So: try again, twice, backing off — enough to outlast a hiccup, not so much
 * that a card with genuinely no picture hammers a CDN on every render. And
 * when it really is not there, say which card it was rather than leaving a
 * grey rectangle, because this is a list you are choosing a card from.
 */
const RETRIES = 2;

export function Art({ src, className, name = "" }) {
  const [tick, setTick] = useState(0);      // bumping this re-requests the file
  const [failed, setFailed] = useState(false);
  const tries = useRef(0);
  const timer = useRef(null);

  // A different card in the same slot starts its own attempts.
  useEffect(() => {
    tries.current = 0;
    setTick(0);
    setFailed(false);
    return () => clearTimeout(timer.current);
  }, [src]);

  const onError = () => {
    if (tries.current >= RETRIES) { setFailed(true); return; }
    tries.current += 1;
    // 400ms, then 800ms. Long enough to outlast a hiccup, short enough that
    // nobody watches a blank square wondering.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setTick((t) => t + 1), tries.current * 400);
  };

  if (!src || failed) {
    /* Said, not just hovered. title on a non-focusable span with the glyph
       hidden meant assistive tech got nothing and a keyboard could not reach
       it — the same cascade problem as the add buttons, one layer down. */
    return (
      <span
        className={`${className} art-missing`}
        role="img"
        aria-label={name ? `No picture for ${name}` : "No picture"}
        title={name ? `No picture for ${name}` : undefined}
      >
        <span aria-hidden="true">▢</span>
      </span>
    );
  }

  return (
    <span className={className}>
      <img
        /* The key restarts the request. Without it React reuses the element
           that already failed and the browser never asks again. */
        key={`${src}#${tick}`}
        src={src}
        alt=""
        onError={onError}
      />
    </span>
  );
}

function Result({ card, isOnePiece, onPick, picked }) {
  const item = asItem(card, isOnePiece);
  return (
    <li className={`sbd-hit${picked ? " is-in" : ""}`}>
      {/* title is a tooltip, not a name: the button's own text wins the
          accessible-name cascade, so a screen reader heard "Umbreon VMAX
          Evolving Skies · 215" and never the word that says what pressing it
          does. The label says it; the tooltip stays for the mouse. */}
      <button
        type="button"
        onClick={() => onPick(item)}
        title={`Add ${item.name}`}
        /* The number goes in, because it is the field that separates prints:
           four consecutive results all announced as "Add Mega Charizard X ex,
           Phantasmal Flames" while being 013, 109, 125 and 130 — four cards at
           four prices. It is on screen, and the help text already tells people
           to lean on it. */
        aria-label={[
          `Add ${item.name}`,
          item.number ? `number ${item.number}` : "",
          item.set,
        ].filter(Boolean).join(", ")}
      >
        <Art src={item.image} className="sbd-hit-art" name={item.name} />
        <span className="sbd-hit-text">
          {item.name}
          <span className="sbd-hit-set">
            {item.set}
            {item.number && !isOnePiece && ` · ${item.number}`}
          </span>
        </span>
        <span className="sbd-hit-add" aria-hidden="true">{picked ? "✓" : "+"}</span>
      </button>
    </li>
  );
}

/**
 * @param onPick      called with the card, in the shape asItem returns
 * @param isPicked    optional (key) => bool, to tick what is already taken
 * @param clearOnPick empty the box after choosing — right where a screen takes
 *                    one card at a time, wrong where somebody is adding ten
 */
export default function CardFinder({
  isOnePiece,
  onPick,
  isPicked,
  placeholder,
  clearOnPick = false,
}) {
  const [term, setTerm] = useState("");
  const [hits, setHits] = useState([]);
  const [state, setState] = useState("idle");
  /* What the server had to do to answer: the nearest real name to a mistyped
     one, or the set it read out of the query. Both change which cards came
     back, so both are said rather than silently applied. */
  const [note, setNote] = useState(null);
  /* The One Piece set names, so a set can be searched by what it is called
     rather than only by its code. Fetched once and kept, so this stays a
     search that answers as you type off things already here. */
  const [opSets, setOpSets] = useState(knownSets);
  /* DON!! cards, which are in no catalogue because they carry no number. This
     screen is how a card gets into a submission or onto a stack, and one of
     them sells for $722 — being unable to type it in was the gap. */
  const [opDon, setOpDon] = useState(knownDon);
  const runRef = useRef(0);

  useEffect(() => {
    if (!isOnePiece) return undefined;
    let live = true;
    // Empty means the source was unreachable, which is nothing new to render.
    loadSets().then((s) => { if (live && s && Object.keys(s).length) setOpSets(s); });
    loadDon().then((d) => { if (live && d.length) setOpDon(d); });
    return () => { live = false; };
  }, [isOnePiece]);

  useEffect(() => {
    // Invalidate an already-running request even when the next value is too
    // short to issue another one (for example, clearing the input).
    const run = ++runRef.current;
    const q = term.trim();
    if (q.length < MIN_CHARS) { setHits([]); setState("idle"); setNote(null); return undefined; }

    /* One Piece searches a catalogue bundled into the app, so there is nothing
       to wait for and no request to spare — it answers as you type. */
    if (isOnePiece) {
      /* DON cards first, and few: this is a short list you pick one card from,
         not a grid to browse, and "don" matches sixty-two Donquixote prints
         that would otherwise push the cards named by the word off the end. */
      const dons = searchDon(q, opDon).slice(0, 4).map((d) => ({ ...d, don: true }));
      const cards = searchOnePiece(q, opSets).slice(0, MAX_SHOWN - dons.length);
      setHits([...dons, ...cards]);
      setState("ready");
      setNote(null);
      return undefined;
    }

    setState("loading");
    const t = setTimeout(async () => {
      try {
        const found = await findCards(q);
        if (run !== runRef.current) return;
        setHits(found.slice(0, MAX_SHOWN));
        setNote(
          found.narrowedTo
            ? `In ${found.narrowedTo}`
            : found.correctedTo
            ? `Showing ${found.correctedTo}`
            : null
        );
        setState("ready");
      } catch {
        if (run === runRef.current) setState("error");
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term, isOnePiece, opSets, opDon]);

  const pick = (item) => {
    onPick(item);
    if (clearOnPick) { setTerm(""); setHits([]); setState("idle"); setNote(null); }
  };

  return (
    <>
      <div className="sbd-search">
        <input
          className="sbd-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={
            placeholder ||
            (isOnePiece
              ? "Search a card — Luffy, OP13-118…"
              : "Search a card — name, number, or set")
          }
          aria-label="Search for a card to add"
          spellCheck={false}
        />
        {term && (
          <button type="button" className="sbd-clear" onClick={() => setTerm("")} aria-label="Clear the search">
            ×
          </button>
        )}
      </div>

      {state === "loading" && <p className="msg-empty">Looking…</p>}
      {state === "error" && <p className="msg-error">Could not search just now.</p>}
      {state === "ready" && hits.length === 0 && (
        <p className="msg-empty">
          Nothing matched “{term.trim()}”. A name works, and so does a set —
          try one or the other on its own.
        </p>
      )}

      {state === "ready" && note && hits.length > 0 && <p className="sbd-note">{note}</p>}

      {hits.length > 0 && (
        <ul className="sbd-hits">
          {hits.map((c) => (
            <Result
              key={c.id}
              card={c}
              isOnePiece={isOnePiece}
              onPick={pick}
              picked={isPicked ? isPicked(c.id) : false}
            />
          ))}
        </ul>
      )}
    </>
  );
}
